import { Button } from "@/components/ui/button";
import { FieldError, Field, FieldLabel } from "@/components/ui/field";
import { Heading } from "@/components/heading";
import { Input } from "@/components/ui/input";
import { TextLink, Text } from "@/components/text";
import {
	createFileRoute,
	redirect,
	useLoaderData,
	useNavigate,
	useRouteContext,
	useRouter,
	useSearch,
} from "@tanstack/react-router";
import { useForm } from "@tanstack/react-form";
import { z } from "zod";
import { Spinner } from "@/components/ui/spinner";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { useServerFn } from "@tanstack/react-start";
import { login as loginHandler } from "@/handlers/login";
import { Icon } from "@/components/icon";
import { AnalyticsEvents, useAnalytics } from "@/utils/analytics";
import { toast } from "sonner";
import { commitLocalUpdate, fetchQuery, graphql } from "react-relay";
import type { login_inviteQuery } from "./__generated__/login_inviteQuery.graphql";
import { environment } from "@/relay/environment";
import { useEffect, useState } from "react";
import {
	InputGroup,
	InputGroupAddon,
	InputGroupButton,
	InputGroupInput,
} from "@/components/ui/input-group";

export const Route = createFileRoute("/_auth/(no-auth)/login")({
	component: RouteComponent,
	validateSearch: z.object({
		code: z.string().optional(),
		redirectTo: z.string().optional(),
	}),
	loaderDeps: ({ search }) => [search.code, search.redirectTo],
	loader: async ({ context: { club }, deps: [code, redirectTo] }) => {
		if (code) {
			const data = await fetchQuery<login_inviteQuery>(
				environment,
				graphql`
     	    query login_inviteQuery($code: String!) {
      				invite(code: $code) {
                email
                code
     	          club {
     					    id
      				  }
                status
                user {
                  role
                }
      				}
     			}
      		`,
				{ code },
			).toPromise();

			const isInviteValid =
				data?.invite?.club?.id === club?.id &&
				data?.invite?.status === "PENDING";

			if (isInviteValid) {
				if (data.invite.user.role !== "authenticated") {
					throw redirect({
						to: "/cadastro",
						search: {
							code,
							redirectTo,
						},
					});
				}

				return { invite: data.invite };
			}
		}

		return { invite: undefined };
	},
});

function RouteComponent() {
	const { invite } = useLoaderData({ from: Route.id });
	const { club } = useRouteContext({ from: "__root__" });
	const { code, redirectTo } = useSearch({ from: Route.id });
	const router = useRouter();
	const navigate = useNavigate({ from: Route.fullPath });
	const [isPasswordVisible, setIsPasswordVisible] = useState(false);

	const { track } = useAnalytics();
	const login = useServerFn(loginHandler);
	const form = useForm({
		defaultValues: {
			username: invite?.email || "",
			password: "",
		},
		validators: {
			onChange: z.object({
				username: z.string().email("Email inválido"),
				password: z.string().min(1, "A senha é obrigatória"),
			}),
		},
		onSubmit: async ({ value }) => {
			return await login({
				data: {
					...value,
					code: invite?.code,
					// @ts-expect-error
					as: typeof window?.as === "string" ? window.as : undefined,
				},
			})
				.then(async (data) => {
					track(AnalyticsEvents.AUTH_LOGIN_COMPLETED, {
						method: "email",
						has_invite: !!invite?.code,
					});

					commitLocalUpdate(environment, (store) => store.invalidateStore());

					await router.invalidate();

					if (data) {
						navigate({
							to: redirectTo || "/",
						});
					}
				})
				.catch((err) => {
					track(AnalyticsEvents.AUTH_LOGIN_FAILED, {
						error: err.message || "unknown",
					});

					if (err.message?.includes("not found")) {
						toast.error(
							"Não encontramos uma conta com esse email. Faça seu cadastro para continuar.",
						);
					} else if (err.message?.includes("credentials")) {
						toast.error("Usuário ou senha inválidos");
					} else {
						toast.error(
							"Não foi possível fazer login. Verifique os dados inseridos e tente novamente.",
						);
					}

					throw err;
				});
		},
	});

	useEffect(() => {
		if (!invite && code && !form.state.isSubmitted) {
			const timeout = setTimeout(() => {
				toast.error("Esse convite já foi utilizado.");
			}, 300);

			return () => {
				clearTimeout(timeout);
			};
		}
	}, [invite, code, form.state.isSubmitted]);

	return (
		<div className="flex justify-center w-full">
			<div className="flex w-full flex-col gap-8 justify-center p-6 max-w-[550px]">
				<div className="size-12">
					<Avatar>
						<AvatarFallback>
							{club?.name.slice(0, 2).toUpperCase()}
						</AvatarFallback>
						<AvatarImage src={club?.logo?.signedUrl} />
					</Avatar>
				</div>

				<Heading>Bem-vindo de Volta!</Heading>

				<form
					className="flex flex-col gap-8"
					onSubmit={(e) => {
						e.preventDefault();
						e.stopPropagation();
						form.handleSubmit();
					}}
				>
					<form.Field name="username">
						{(field) => (
							<Field>
								<FieldLabel htmlFor={field.name}>Email</FieldLabel>
								<Input
									id={field.name}
									name={field.name}
									value={field.state.value}
									onBlur={field.handleBlur}
									onChange={(e) => field.handleChange(e.target.value)}
									type="email"
									placeholder="Email"
									disabled={!!invite?.email}
									aria-invalid={
										!!(field.state.meta.isTouched && field.state.meta.errors[0])
									}
								/>

								{field.state.meta.isTouched && field.state.meta.errors[0] ? (
									<FieldError errors={field.state.meta.errors} />
								) : null}
							</Field>
						)}
					</form.Field>

					<form.Field name="password">
						{(field) => (
							<Field>
								<FieldLabel htmlFor={field.name}>Senha</FieldLabel>
								<InputGroup>
									<InputGroupInput
										id={field.name}
										name={field.name}
										value={field.state.value}
										onBlur={field.handleBlur}
										onChange={(e) => field.handleChange(e.target.value)}
										type={isPasswordVisible ? "text" : "password"}
										placeholder="Senha"
										autoCapitalize="off"
										autoCorrect="off"
										aria-invalid={
											!!(
												field.state.meta.isTouched && field.state.meta.errors[0]
											)
										}
									/>

									<InputGroupAddon align="inline-end">
										<InputGroupButton
											tabIndex={-1}
											type="button"
											onClick={() => setIsPasswordVisible((v) => !v)}
										>
											<Icon
												name={isPasswordVisible ? "EyeOff" : "Eye"}
												className="size-4"
											/>
										</InputGroupButton>
									</InputGroupAddon>
								</InputGroup>

								{field.state.meta.isTouched && field.state.meta.errors[0] ? (
									<FieldError errors={field.state.meta.errors} />
								) : null}
							</Field>
						)}
					</form.Field>

					<TextLink to="/esqueci-a-senha">Esqueci a senha</TextLink>

					<form.Subscribe
						selector={(state) => [state.isSubmitting, state.isSubmitSuccessful]}
					>
						{([isSubmitting, isSubmitSuccessful]) => (
							<div className="flex flex-col gap-6">
								<Button
									type="submit"
									color={isSubmitSuccessful ? "emerald" : "primary"}
									disabled={isSubmitting || isSubmitSuccessful}
									className="flex items-center justify-center gap-2"
								>
									{isSubmitSuccessful ? (
										<Icon name="CircleCheck" className="size-4" />
									) : null}

									{!isSubmitSuccessful && (
										<>Entrar {isSubmitting ? <Spinner /> : null}</>
									)}
								</Button>

								{club?.visibility === "PUBLIC" ? (
									<Text className="font-medium">
										Ainda não tem uma conta?{" "}
										<TextLink to="/cadastro" disabled={isSubmitting}>
											Crie agora
										</TextLink>
									</Text>
								) : null}
							</div>
						)}
					</form.Subscribe>
				</form>
			</div>
		</div>
	);
}
